import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCompetitionStore } from './competitionStore';

export interface WordWizardStats {
  totalWins: number;
  skillWins: number;      // wins in ≤3 guesses
  currentStreak: number;
  bestStreak: number;
  xp: number;
  level: number;          // 1–6
}

const WW_DEFAULT: WordWizardStats = {
  totalWins: 0, skillWins: 0, currentStreak: 0, bestStreak: 0, xp: 0, level: 1,
};

function calcWWLevel(stats: WordWizardStats): number {
  const { totalWins, skillWins, bestStreak } = stats;
  if (totalWins >= 75 && skillWins >= 20) return 6;
  if (totalWins >= 50 && bestStreak >= 5)  return 5;
  if (totalWins >= 30 && skillWins >= 10)  return 4;
  if (totalWins >= 15 && skillWins >= 3)   return 3;
  if (totalWins >= 5)                      return 2;
  return 1;
}

export interface GameRecord {
  gameType: string;
  score: number;
  reactionTimeMs?: number;
  accuracy?: number;
  /** Highest difficulty tier (1-5) reached during this session. Quiz-only. */
  tier?: 1 | 2 | 3 | 4 | 5;
  timestamp: string;
  childId: string;
}

interface PersonalBest {
  score: number;
  reactionTimeMs?: number;
  accuracy?: number;
  timestamp: string;
}

export interface GamesState {
  /** Personal bests keyed by `${childId}:${gameType}` */
  personalBests: Record<string, PersonalBest>;
  /** Last 50 game records */
  history: GameRecord[];
  /** Tracks which games were played today keyed by `${childId}:${date}` */
  dailyPlayed: Record<string, string[]>;
  /** Game of the day index (rotates daily) */
  lastDailyGameDate: string;
  dailyGameIndex: number;
  /** Word Wizard per-child progression */
  wordWizardStats: Record<string, WordWizardStats>;
  /** Word Jumble sequential progress — index into the ordered word bank per child */
  wordJumbleIndex: Record<string, number>;
  /** Lifetime session count per child — never resets, survives word-bank wrap-around */
  wordJumbleSession: Record<string, number>;
  /** Generic level progress for any game. Key: `${childId}:${gameType}` */
  gameLevel: Record<string, number>;
  /** Highest difficulty tier (1-5) ever reached per child per quiz. Key: `${childId}:${quizType}` */
  highestQuizTier: Record<string, 1 | 2 | 3 | 4 | 5>;

  addRecord: (record: GameRecord) => void;
  getPersonalBest: (childId: string, gameType: string) => PersonalBest | null;
  getHistory: (childId: string, gameType?: string) => GameRecord[];
  getGamesPlayedToday: (childId: string) => string[];
  getDailyGameIndex: () => number;
  getHighestQuizTier: (childId: string, quizType: string) => 1 | 2 | 3 | 4 | 5 | 0;
  /** Records a tier reach. Only writes if higher than previously recorded. */
  recordHighestQuizTier: (childId: string, quizType: string, tier: 1 | 2 | 3 | 4 | 5) => void;
  getQuizTiersForChild: (childId: string) => Record<string, 1 | 2 | 3 | 4 | 5>;
  /** Returns { prevLevel, newLevel, xpGained }. Pass guessCount=null for a loss. */
  updateWordWizard: (childId: string, guessCount: number | null) => { prevLevel: number; newLevel: number; xpGained: number };
  getWordWizardStats: (childId: string) => WordWizardStats;
  getWordJumbleIndex: (childId: string) => number;
  advanceWordJumble: (childId: string, count: number, poolSize: number) => void;
  getWordJumbleSession: (childId: string) => number;
  incrementWordJumbleSession: (childId: string) => void;
  getGameLevel: (childId: string, gameType: string) => number;
  setGameLevel: (childId: string, gameType: string, level: number) => void;
}

const GAME_TYPES = ['reaction-color', 'reaction-math', 'reaction-pattern', 'speed-math', 'whack-a-mole', 'typing-challenge'];

export const useGamesStore = create<GamesState>()(
  persist(
    (set, get) => ({
      personalBests: {},
      history: [],
      dailyPlayed: {},
      lastDailyGameDate: '',
      dailyGameIndex: 0,
      wordWizardStats: {},
      wordJumbleIndex: {},
      wordJumbleSession: {},
      gameLevel: {},
      highestQuizTier: {},

      addRecord: (record) => {
        const key = `${record.childId}:${record.gameType}`;
        const current = get().personalBests[key];
        const isNewBest = !current || record.score > current.score;

        const today = new Date().toISOString().split('T')[0];
        const dailyKey = `${record.childId}:${today}`;
        const todayGames = get().dailyPlayed[dailyKey] || [];

        set(state => ({
          personalBests: isNewBest
            ? {
                ...state.personalBests,
                [key]: {
                  score: record.score,
                  reactionTimeMs: record.reactionTimeMs,
                  accuracy: record.accuracy,
                  timestamp: record.timestamp,
                },
              }
            : state.personalBests,
          history: [...state.history, record].slice(-50),
          dailyPlayed: {
            ...state.dailyPlayed,
            [dailyKey]: [...new Set([...todayGames, record.gameType])],
          },
        }));

        // Bridge to sibling competitions — submit when score is a personal best
        if (isNewBest && record.childId) {
          const comp = useCompetitionStore.getState();
          for (const evt of comp.getActiveEventsForQuiz(record.gameType)) {
            const existing = comp.results.filter(r => r.eventId === evt.id && r.childId === record.childId);
            const best = existing.reduce((m, r) => Math.max(m, r.score), -1);
            if (record.score > best) comp.submitResult(evt.id, record.childId, record.score, []);
          }
        }
      },

      getPersonalBest: (childId, gameType) => {
        return get().personalBests[`${childId}:${gameType}`] || null;
      },

      getHistory: (childId, gameType) => {
        return get().history.filter(
          r => r.childId === childId && (!gameType || r.gameType === gameType)
        );
      },

      getGamesPlayedToday: (childId) => {
        const today = new Date().toISOString().split('T')[0];
        return get().dailyPlayed[`${childId}:${today}`] || [];
      },

      getWordWizardStats: (childId) => {
        return get().wordWizardStats[childId] ?? { ...WW_DEFAULT };
      },

      updateWordWizard: (childId, guessCount) => {
        const prev = get().wordWizardStats[childId] ?? { ...WW_DEFAULT };
        const prevLevel = prev.level;
        const won = guessCount !== null;
        const xpGained = won ? (guessCount <= 2 ? 100 : guessCount <= 4 ? 60 : 20) : 0;
        const next: WordWizardStats = {
          totalWins:     prev.totalWins + (won ? 1 : 0),
          skillWins:     prev.skillWins + (won && guessCount <= 3 ? 1 : 0),
          currentStreak: won ? prev.currentStreak + 1 : 0,
          bestStreak:    won ? Math.max(prev.bestStreak, prev.currentStreak + 1) : prev.bestStreak,
          xp:            prev.xp + xpGained,
          level:         1,
        };
        next.level = calcWWLevel(next);
        set(state => ({ wordWizardStats: { ...state.wordWizardStats, [childId]: next } }));
        return { prevLevel, newLevel: next.level, xpGained };
      },

      getWordJumbleIndex: (childId) => get().wordJumbleIndex[childId] ?? 0,

      advanceWordJumble: (childId, count, poolSize) => {
        const current = get().wordJumbleIndex[childId] ?? 0;
        set(state => ({
          wordJumbleIndex: {
            ...state.wordJumbleIndex,
            [childId]: (current + count) % poolSize,
          },
        }));
      },

      getWordJumbleSession: (childId) => get().wordJumbleSession[childId] ?? 1,

      incrementWordJumbleSession: (childId) => {
        const current = get().wordJumbleSession[childId] ?? 1;
        set(state => ({
          wordJumbleSession: { ...state.wordJumbleSession, [childId]: current + 1 },
        }));
      },

      getGameLevel: (childId, gameType) => get().gameLevel[`${childId}:${gameType}`] ?? 0,

      setGameLevel: (childId, gameType, level) => {
        set(state => ({
          gameLevel: { ...state.gameLevel, [`${childId}:${gameType}`]: level },
        }));
      },

      getHighestQuizTier: (childId, quizType) => {
        return get().highestQuizTier[`${childId}:${quizType}`] ?? 0;
      },

      recordHighestQuizTier: (childId, quizType, tier) => {
        const key = `${childId}:${quizType}`;
        const prev = get().highestQuizTier[key] ?? 0;
        if (tier <= prev) return;
        set(state => ({
          highestQuizTier: { ...state.highestQuizTier, [key]: tier },
        }));
      },

      getQuizTiersForChild: (childId) => {
        const prefix = `${childId}:`;
        const result: Record<string, 1 | 2 | 3 | 4 | 5> = {};
        for (const [k, v] of Object.entries(get().highestQuizTier)) {
          if (k.startsWith(prefix)) result[k.slice(prefix.length)] = v;
        }
        return result;
      },

      getDailyGameIndex: () => {
        const today = new Date().toISOString().split('T')[0];
        const state = get();
        if (state.lastDailyGameDate !== today) {
          // Rotate to next game
          const newIndex = (state.dailyGameIndex + 1) % GAME_TYPES.length;
          set({ lastDailyGameDate: today, dailyGameIndex: newIndex });
          return newIndex;
        }
        return state.dailyGameIndex;
      },
    }),
    { name: 'homeschool-games', version: 0, migrate: (state) => state }
  )
);
